import type { SfuControl } from '../hooks/sfu-control'
import { vclog } from '../hooks/vc-log'

/**
 * Keeps the SERVER's idea of "is this producer paused" matching ours.
 *
 * Muting is two separate facts. `producer.pause()` is local — it disables the
 * track, so nobody hears you. `pauseProducer` on the SFU is what every OTHER
 * participant's mute indicator is drawn from. Only the first is guaranteed; the
 * second is a request over a socket, and it can fail.
 *
 * It used to be sent from the same React effect that flipped the local state,
 * with its rejection dropped into an empty catch. Two things follow from that,
 * and both have bitten this app before:
 *
 *  - **Anyone who pauses a producer somewhere else has to remember the second
 *    call.** A local `pause()` alone reaches nobody, which is exactly how
 *    camera-off used to render as a black tile instead of a camera-off tile.
 *  - **A failed send left you silent and looking unmuted**, with nothing said.
 *
 * So the notify is bound to the producer's own `observer` instead. Any pause,
 * from anywhere — an effect, a moderator action, a future code path nobody has
 * written yet — notifies, because it is the producer that reports it rather
 * than the caller that remembers to. That is edumeet's design and it is the
 * right one.
 *
 * On top of it, three things a plain observer binding does not give you:
 *
 *  - **Dedupe.** `Producer.pause()` has no early return when already paused; it
 *    emits every time it is called. Our mute effect re-runs whenever the
 *    producer object changes, so a bare binding would re-send on every device
 *    switch. This tracks the state the server last acknowledged and sends only
 *    on a real change.
 *  - **Retry.** A dropped request is retried before it is given up on.
 *  - **Reconnect.** A socket blip loses whatever was in flight, and the
 *    consumer-side re-sync in `useSfuJoin` does not cover producers. Mute,
 *    blip, and you are back in the room looking unmuted. `resync()` re-asserts
 *    every producer's desired state.
 */

/**
 * The slice of a mediasoup `Producer` this needs, declared structurally.
 *
 * Deliberately not `import type { Producer } from 'mediasoup-client/lib/types'`:
 * that path does not resolve under this project's `moduleResolution: bundler`
 * setup — the same two errors sit at the top of `useLocalMedia` today — and
 * importing it here would add a third. Structural typing costs nothing, keeps
 * the count where it is, and makes this testable with a plain object.
 */
type PausableProducer = {
  id: string
  paused: boolean
  /**
   * Optional because the tests build these by hand, and because it is only
   * read for the diagnostic above — a producer the client has closed cannot be
   * synced and its failure means nothing.
   */
  closed?: boolean
  observer: {
    on(event: 'pause' | 'resume', listener: () => void): void
    off(event: 'pause' | 'resume', listener: () => void): void
    once(event: 'close', listener: () => void): void
  }
}

/** The subset needed to move a producer's local pause state. */
type TogglableProducer = {
  paused: boolean
  pause(): void
  resume(): void
}

/**
 * Move a producer's pause state — but only when it is not already there.
 *
 * `Producer.pause()` and `Producer.resume()` have NO early return when the
 * producer is already in the requested state. They set the flag and fire their
 * events regardless. That was free when pausing meant `track.enabled = false`,
 * and it stopped being free the moment `zeroRtpOnPause` was turned on: every
 * call then runs `pauseSending`/`resumeSending`, which sets the transceiver's
 * direction and performs a full offer/answer cycle on the send transport.
 *
 * That transport carries the microphone, the camera and the screen share
 * together, so a redundant call renegotiates all three. The mute effects called
 * these unconditionally on every run — including once at join, on producers
 * that had never been paused — and the audible result was audio breaking up.
 *
 * So the idempotence lives here, in one place, rather than as a guard at each
 * call site where the next caller would forget it.
 *
 * Returns whether anything actually changed, so callers can log transitions
 * instead of logging every render.
 */
export function setProducerPaused(producer: TogglableProducer, paused: boolean): boolean {
  if (producer.paused === paused) return false
  if (paused) producer.pause()
  else producer.resume()
  return true
}

/** Backoff between attempts. Short — this is a mute indicator, not a download. */
const RETRY_DELAYS_MS = [400, 1200, 3000]

type Entry = {
  producer: PausableProducer
  kind: 'audio' | 'video'
  /** What we want the server to believe. */
  desired: boolean
  /** What the server last confirmed. `null` = unknown, so re-send regardless. */
  acknowledged: boolean | null
  inFlight: boolean
  attempt: number
  timer: ReturnType<typeof setTimeout> | null
  /** Logged this give-up once already; do not repeat until something changes. */
  reported: boolean
  detach: () => void
}

export type ProducerPauseSync = {
  /** Bind to a producer. Safe to call once per producer; unbinds itself on close. */
  track: (producer: PausableProducer, kind: 'audio' | 'video') => void
  /** Re-assert every desired state — call after the control socket reconnects. */
  resync: () => void
  /** Drop everything. */
  dispose: () => void
}

export function createProducerPauseSync(control: SfuControl): ProducerPauseSync {
  const entries = new Map<string, Entry>()
  let disposed = false

  function clearTimer(entry: Entry): void {
    if (!entry.timer) return
    clearTimeout(entry.timer)
    entry.timer = null
  }

  function report(entry: Entry): void {
    if (entry.reported) return
    entry.reported = true
    // This never reaches the SFU rejecting the pause/resume itself: room.js
    // looks the producer up and no-ops when it finds nothing, resolving either
    // way. The only thing that can actually fail here is the control socket,
    // and pause/resume is applied locally regardless of it (`zeroRtpOnPause`),
    // so the room's audio and video are unaffected while this is in flight.
    // `resync()` re-asserts the desired state once the socket reconnects, which
    // repairs the server's view without any input from the user. So this is
    // logged for diagnosis rather than surfaced: at worst another participant
    // sees a stale mute icon for a few seconds, and there is nothing the user
    // could do about it anyway.
    vclog('producer', 'pause/resume notify GAVE UP', {
      producerId: entry.producer.id,
      kind: entry.kind,
      desiredPaused: entry.desired,
      lastAcknowledged: entry.acknowledged,
      attempts: entry.attempt,
      stale: entry.producer.closed ?? null,
      trackedProducers: entries.size,
    })
  }

  function flush(entry: Entry): void {
    if (disposed || entry.inFlight) return
    if (entry.acknowledged === entry.desired) return

    const target = entry.desired
    entry.inFlight = true
    const send = target
      ? control.pauseProducer(entry.producer.id)
      : control.resumeProducer(entry.producer.id)

    send
      .then(() => {
        entry.inFlight = false
        entry.acknowledged = target
        entry.attempt = 0
        entry.reported = false
        // Changed again while we were in flight — go round once more.
        if (entry.desired !== target) flush(entry)
      })
      .catch((err) => {
        entry.inFlight = false
        // Someone else already moved the goalposts; the new state is what
        // matters and it gets its own attempts.
        if (entry.desired !== target) {
          entry.attempt = 0
          flush(entry)
          return
        }
        const delay = RETRY_DELAYS_MS[entry.attempt]
        entry.attempt++
        vclog('producer', 'producer pause/resume notify FAILED', {
          producerId: entry.producer.id,
          kind: entry.kind,
          paused: target,
          attempt: entry.attempt,
          willRetryIn: delay ?? null,
          error: (err as Error).message,
        })
        if (delay === undefined) {
          // Out of attempts. Stop here rather than retrying forever: the next
          // toggle, or the next reconnect, is a better trigger than a timer.
          report(entry)
          return
        }
        clearTimer(entry)
        entry.timer = setTimeout(() => {
          entry.timer = null
          flush(entry)
        }, delay)
      })
  }

  function setDesired(entry: Entry, paused: boolean): void {
    if (entry.desired === paused) return
    entry.desired = paused
    // A fresh decision deserves fresh attempts, and a fresh chance to complain.
    entry.attempt = 0
    entry.reported = false
    clearTimer(entry)
    flush(entry)
  }

  return {
    track(producer, kind) {
      if (disposed || entries.has(producer.id)) return

      const entry: Entry = {
        producer,
        kind,
        // The SFU creates a producer running, so that is the shared starting
        // point. Anything else arrives as an observer event.
        desired: producer.paused,
        acknowledged: producer.paused ? null : false,
        inFlight: false,
        attempt: 0,
        timer: null,
        reported: false,
        detach: () => {},
      }

      const onPause = () => setDesired(entry, true)
      const onResume = () => setDesired(entry, false)
      const onClose = () => {
        producer.observer.off('pause', onPause)
        producer.observer.off('resume', onResume)
        clearTimer(entry)
        entries.delete(producer.id)
      }

      producer.observer.on('pause', onPause)
      producer.observer.on('resume', onResume)
      producer.observer.once('close', onClose)
      entry.detach = onClose

      entries.set(producer.id, entry)
      // Produced while already paused — joining muted. The server has it
      // running, so this is a real difference, not a no-op.
      flush(entry)
    },

    resync() {
      if (disposed) return
      for (const entry of entries.values()) {
        // Everything we thought the server knew went down with the socket.
        entry.acknowledged = null
        entry.attempt = 0
        entry.reported = false
        clearTimer(entry)
        flush(entry)
      }
    },

    dispose() {
      disposed = true
      for (const entry of [...entries.values()]) entry.detach()
      entries.clear()
    },
  }
}
